import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import api from "../lib/api";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import { KpiCard, DashboardHeader, SectionTable } from "../components/DashboardWidgets";
import { Users, UserPlus, UserMinus, Buildings, ShieldCheck, IdentificationCard } from "@phosphor-icons/react";

const KPI = [
  { key: "total_employees",         label: "Total Employees",   icon: Users,              tone: "blue" },
  { key: "active_employees",        label: "Active",            icon: UserPlus,           tone: "emerald" },
  { key: "inactive_employees",      label: "Inactive",          icon: UserMinus,          tone: "amber" },
  { key: "login_enabled",           label: "Login Enabled",     icon: IdentificationCard, tone: "indigo" },
  { key: "total_departments",       label: "Departments",       icon: Buildings,          tone: "slate" },
  { key: "active_departments",      label: "Active Depts",      icon: Buildings,          tone: "emerald" },
  { key: "total_roles",             label: "Roles",             icon: ShieldCheck,        tone: "blue" },
  { key: "active_roles",            label: "Active Roles",      icon: ShieldCheck,        tone: "emerald" },
  { key: "unassigned_to_department",label: "Unassigned",        icon: UserMinus,          tone: "rose" },
];

const HRDashboard = ({ user, logout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`${API}/dashboard/hr`); setData(r.data); }
    catch { toast.error("Failed to load HR dashboard"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        <div className="p-8">
          <DashboardHeader overline="Human Resources" title="HR Dashboard" subtitle="Employees, departments, roles & reporting hierarchy." onRefresh={load} refreshTestId="hr-refresh-btn" />
          {loading ? <div className="flex items-center justify-center py-20"><div className="loading-spinner"></div></div> : (
            <>
              <div data-testid="hr-kpi-grid" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-10">
                {KPI.map(k => <KpiCard key={k.key} icon={k.icon} label={k.label} tone={k.tone} money={k.money} value={data?.kpis?.[k.key]} testId={`hr-kpi-${k.key}`} />)}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <SectionTable
                  title="By Department" icon={Buildings} testId="hr-by-dept"
                  columns={[{ label: "Department" }, { label: "Employees", align: "right" }]}
                  rows={data?.by_department || []}
                  emptyText="No department mapping yet."
                  renderRow={(r, i) => (
                    <tr key={r.department + i} data-testid={`hr-dept-row-${i}`} className="border-b border-slate-100">
                      <td className="p-3 font-medium">{r.department}</td>
                      <td className="p-3 text-right tabular-nums">{r.count}</td>
                    </tr>
                  )}
                />
                <SectionTable
                  title="By Auth Tier (Role)" icon={ShieldCheck} testId="hr-by-role"
                  columns={[{ label: "Role" }, { label: "Count", align: "right" }]}
                  rows={data?.by_role || []}
                  emptyText="No role data."
                  renderRow={(r, i) => (
                    <tr key={r.role + i} data-testid={`hr-role-row-${i}`} className="border-b border-slate-100">
                      <td className="p-3 text-sm uppercase tracking-wider font-bold text-blue-700">{r.role}</td>
                      <td className="p-3 text-right tabular-nums">{r.count}</td>
                    </tr>
                  )}
                />
              </div>
              <SectionTable
                title="Department Heads" icon={Buildings} testId="hr-dept-heads"
                columns={[{ label: "Department" }, { label: "Head" }, { label: "Status" }, { label: "Employees", align: "right" }]}
                rows={data?.departments || []}
                emptyText="No departments configured."
                renderRow={(r, i) => (
                  <tr key={r.name + i} data-testid={`hr-deptheads-row-${i}`} className="border-b border-slate-100">
                    <td className="p-3 font-medium">{r.name}</td>
                    <td className="p-3 text-sm text-slate-700">{r.head || <span className="text-slate-400">Not assigned</span>}</td>
                    <td className="p-3"><span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border ${r.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>{r.status}</span></td>
                    <td className="p-3 text-right tabular-nums">{r.user_count}</td>
                  </tr>
                )}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HRDashboard;
